import React from 'react';
import {useTranslation} from 'react-i18next';

// 根据用户提供的数据格式定义接口
interface VipWeekCardReward {
  bonusAmount: number;
  day: number;
  scratchCount: number;
  spinCount: number;
}

interface VipWeekCardConfig {
  bonus: number;
  cardId: number;
  cardName: string;
  price: number;
  scratchName: string;
  vipWeekCardRewardList: VipWeekCardReward[];
}

interface WeekCardTableProps {
  weekCardConfigs: VipWeekCardConfig[];
}

const WeekCardTable: React.FC<WeekCardTableProps> = ({weekCardConfigs}) => {
  const {t} = useTranslation();

  // 按cardName分组并排序
  const groupedConfigs = React.useMemo(() => {
    const groups = {
      silver: null as VipWeekCardConfig | null,
      gold: null as VipWeekCardConfig | null,
      diamond: null as VipWeekCardConfig | null,
    };

    weekCardConfigs.forEach(config => {
      // 不要用cardName 来判断，用cardId来判断
      if (config.cardId === 1) {
        groups.silver = config;
      } else if (config.cardId === 2) {
        groups.gold = config;
      } else if (config.cardId === 3) {
        groups.diamond = config;
      }
    });

    return groups;
  }, [weekCardConfigs]);

  // 生成D1-D7的表格数据
  const tableData = React.useMemo(() => {
    const data = [];
    const days = 7; // D1-D7

    for (let day = 1; day <= days; day++) {
      const row = {
        type: `D${day}`,
        day: day,
        silver: null as any,
        gold: null as any,
        diamond: null as any,
      };

      // 为每个card类型查找对应day的奖励数据
      if (groupedConfigs.silver?.vipWeekCardRewardList) {
        const dayReward = groupedConfigs.silver.vipWeekCardRewardList.find(
          (reward: VipWeekCardReward) => reward.day === day,
        );
        if (dayReward) {
          row.silver = {
            price: groupedConfigs.silver.price,
            bonusAmount: dayReward.bonusAmount,
            spinCount: dayReward.spinCount,
            scratchCount: dayReward.scratchCount,
          };
        }
      }

      if (groupedConfigs.gold?.vipWeekCardRewardList) {
        const dayReward = groupedConfigs.gold.vipWeekCardRewardList.find(
          (reward: VipWeekCardReward) => reward.day === day,
        );
        if (dayReward) {
          row.gold = {
            price: groupedConfigs.gold.price,
            bonusAmount: dayReward.bonusAmount,
            spinCount: dayReward.spinCount,
            scratchCount: dayReward.scratchCount,
          };
        }
      }

      if (groupedConfigs.diamond?.vipWeekCardRewardList) {
        const dayReward = groupedConfigs.diamond.vipWeekCardRewardList.find(
          (reward: VipWeekCardReward) => reward.day === day,
        );
        if (dayReward) {
          row.diamond = {
            price: groupedConfigs.diamond.price,
            bonusAmount: dayReward.bonusAmount,
            spinCount: dayReward.spinCount,
            scratchCount: dayReward.scratchCount,
          };
        }
      }

      data.push(row);
    }

    return data;
  }, [groupedConfigs]);

  if (!weekCardConfigs || weekCardConfigs.length === 0) {
    return (
      <div className="flex justify-center items-center py-8">
        <span className="text-gray-500 text-sm">
          {t('recharge.noWeekCardAvailable', 'No week card available')}
        </span>
      </div>
    );
  }

  // 渲染奖励图标和数值
  const renderRewards = (data: any) => {
    if (!data) {
      return null;
    }
    // console.log('data', data);

    return (
      <div className="flex flex-col items-center gap-2">
        <div className="flex items-center text-[#495057] text-xs font-bold gap-[2px]">
          <img
            src={require('@/assets/imgs/recharge/coin.webp')}
            alt="Gold Coin"
            className="w-4 h-4 mr-1"
          />
          <span className="text-[#495057] text-xs font-bold">
            {data.bonusAmount || 0}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* 转盘图标 */}
          <div className="flex items-center gap-[2px]">
            <img
              src={require('@/assets/imgs/recharge/spin.webp')}
              alt="Spin"
              className="w-4 h-4"
            />
            <span className="text-[#495057] text-xs font-bold">
              {data.spinCount || 0}
            </span>
          </div>
          {/* 刮刮卡图标 */}
          <div className="flex items-center gap-[2px]">
            <img
              src={require('@/assets/imgs/recharge/scratch.webp')}
              alt="Scratch"
              className="w-4 h-4"
            />
            <span className="text-[#495057] text-xs font-bold">
              {data.scratchCount || 0}
            </span>
          </div>
        </div>
      </div>
    );
  };
  //   表头名称不是写死的，是列表的cardName
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const tableHeader = React.useMemo(() => {
    // 再加一个type
    return [
      {
        name: 'TYPE',
        value: 'TYPE',
      },
      //   name 取空格的第一个字符串
      ...weekCardConfigs.map(config => {
        const name = config.cardName.split(' ')[0];
        return {
          name,
          value: name,
        };
      }),
    ];
    // 表头名称不是写死的，是列表的cardName
  }, [weekCardConfigs]);

  return (
    // 表头名称不是写死的，是列表的cardName
    <div className="w-full text-[#48505D]">
      {/* 表格容器 */}
      <div className="bg-[#E9ECF5] rounded-xl overflow-hidden border border-[#E9ECF5]">
        <div className="grid grid-cols-4 bg-[#E9ECF5] border-b border-[#E9ECF5]">
          {/* 表头 */}
          {tableHeader.map((header, index) => (
            <div
              key={index}
              className="px-2 py-3 flex items-center justify-center">
              <span className="text-[#48505D] text-xs font-bold tracking-wide">
                {typeof header === 'string' ? header : header.name}
              </span>
            </div>
          ))}
          {/* <div className="px-2 py-3 flex items-center justify-center">
            <span className="text-[#48505D] text-xs font-bold tracking-wide">
              TYPE
            </span>
          </div>
          <div className="px-2 py-3 flex items-center justify-center">
            <span className="text-[#48505D] text-xs font-bold tracking-wide">
              SILVER
            </span>
          </div>
          <div className="px-2 py-3 flex items-center justify-center">
            <span className="text-[#48505D] text-xs font-bold tracking-wide">
              GOLD
            </span>
          </div>
          <div className="px-2 py-3 flex items-center justify-center">
            <span className="text-[#48505D] text-xs font-bold tracking-wide">
              DIAMOND
            </span>
          </div> */}
        </div>

        {/* 表格数据行 */}
        {tableData.map((row, index) => (
          <div
            key={index}
            className={`grid grid-cols-4 ${
              index % 2 === 0 ? 'bg-white' : 'bg-[#F8F9FF]'
            }`}>
            {/* TYPE列 */}
            <div className="px-2 py-3 flex items-center justify-center border-r border-[#E9ECF5]">
              <span className="text-[#48505D] text-xs font-bold">
                {row.type}
              </span>
            </div>

            {/* SILVER列 */}
            <div className="px-2 py-3 flex items-center justify-center border-r border-[#E9ECF5]">
              {row.silver ? (
                renderRewards(row.silver)
              ) : (
                <span className="text-gray-400 text-xs">-</span>
              )}
            </div>

            {/* GOLD列 */}
            <div className="px-2 py-3 flex items-center justify-center border-r border-[#E9ECF5]">
              {row.gold ? (
                renderRewards(row.gold)
              ) : (
                <span className="text-gray-400 text-xs">-</span>
              )}
            </div>

            {/* DIAMOND列 */}
            <div className="px-2 py-3 flex items-center justify-center">
              {row.diamond ? (
                renderRewards(row.diamond)
              ) : (
                <span className="text-gray-400 text-xs">-</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WeekCardTable;
